import { useTranslation } from "react-i18next";
import { Loader2, Clock, CheckCircle2, AlertTriangle } from "lucide-react";
import type { MediaJob } from "@arc/contracts";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const PHASE = {
  queued: { labelKey: "manualEditor.job.queued", Icon: Clock, className: "bg-muted text-muted-foreground", spin: false, hintKey: "manualEditor.job.queuedHint" },
  processing: { labelKey: "manualEditor.job.processing", Icon: Loader2, className: "bg-attention/10 text-attention", spin: true, hintKey: "manualEditor.job.processingHint" },
  finished: { labelKey: "manualEditor.job.ready", Icon: CheckCircle2, className: "bg-success/10 text-success dark:bg-success/15", spin: false, hintKey: "manualEditor.job.finishedHint" },
  failed: { labelKey: "manualEditor.job.failed", Icon: AlertTriangle, className: "bg-destructive/10 text-destructive", spin: false, hintKey: "manualEditor.job.failedHint" },
} as const;

/**
 * Background-processing state for ONE step (ADR-026 Phase 6).
 *
 * Cutting a video is asynchronous, so without this the step just sits there looking unchanged and the only way to
 * learn anything was to reload and guess. The three phases the author actually distinguishes are **queued**
 * (nothing is happening yet), **processing** (a worker has it) and **finished** — which is why the server derives the
 * phase from `started_at` rather than exposing the raw status, where "queued" and "processing" are the same value.
 *
 * Glyph AND tint AND text, never colour alone (the repo rule), and the spinner turns only while something is really
 * running — a spinner that never stops teaches people to ignore it.
 */
export function StepJobBadge({ job, attachedFileId, className }: { job: MediaJob; attachedFileId?: number | null; className?: string }) {
  const { t } = useTranslation();
  const meta = PHASE[job.phase];
  const Icon = meta.Icon;
  // **"Ready" does not always mean "attached" (diff-panel R12-F3).** `landVideoCut` re-verifies before re-pointing the
  // step, and deliberately does NOT attach when the manual moved out of the authorized facility, or when the step's
  // media changed under the job — the cut is still produced and kept as a library asset, and the job is `done` either
  // way. Telling the author "the media below is the new version" while the step still shows the OLD media is simply
  // false. The step's current media file id is the honest test: if it is not the job's output, the cut went to the
  // library. (No wire change — the editor already knows both ids.)
  const landedOnStep = job.fileId != null && attachedFileId != null && job.fileId === attachedFileId;
  // Compare KEYS, not rendered text: the rendered strings differ per language, so a text comparison would
  // silently stop matching the moment the UI is German.
  const hint =
    meta.hintKey === PHASE.finished.hintKey && !landedOnStep
      ? t("manualEditor.job.finishedSavedHint")
      : t(meta.hintKey);
  return (
    <TooltipTrigger delay={300} excludeFromTabOrder>
      <span
        className={cn("inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[0.7rem] font-medium", meta.className, className)}
        aria-label={t("manualEditor.mediaProcessingLabelkey", { labelKey: t(meta.labelKey) })}
      >
        <Icon aria-hidden="true" className={cn("size-3", meta.spin && "animate-spin")} />
        {t(meta.labelKey)}
      </span>
      <Tooltip>
        <span className="block">{hint}</span>
        {job.phase === "failed" && job.failedReason ? <span className="text-muted-foreground mt-0.5 block text-xs">{job.failedReason}</span> : null}
      </Tooltip>
    </TooltipTrigger>
  );
}
